import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../api/client";

// CSS mirrors the other HRMS pages' tokens 1:1 so this reads as the same module.
const CSS = `
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
:root {
  --bg: #f0f4fc; --surface: #ffffff; --surface2: #f4f8ff;
  --border: #c0d0eb; --border2: #d5e3f5;
  --accent: #0284c7; --text: #0d1a36; --text2: #2a4a78; --muted: #6080aa;
  --danger: #dc2626; --danger-bg: rgba(220,38,38,.07); --danger-border: rgba(220,38,38,.22);
  --warn: #b45309; --warn-bg: rgba(180,83,9,.07);
  --ok: #047857; --ok-bg: rgba(4,120,87,.07);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  color-scheme: light;
}
body { background: var(--bg); color: var(--text); }
.adm-topbar { position: sticky; top: 0; z-index: 100; height: 52px; display: flex; align-items: center; background: rgba(240,244,252,.92); backdrop-filter: blur(12px); border-bottom: 1px solid var(--border2); }
.adm-back { width: 52px; height: 52px; display: flex; align-items: center; justify-content: center; background: none; border: none; border-right: 1px solid var(--border2); color: var(--text2); cursor: pointer; font-size: 16px; flex-shrink: 0; transition: color .15s, background .15s; }
.adm-back:hover { color: var(--text); background: var(--surface); }
.adm-brand { display: flex; align-items: center; gap: 10px; padding: 0 20px; flex: 1; }
.adm-brand-mark { width: 26px; height: 26px; border-radius: 6px; background: linear-gradient(135deg, #0284c7, #2563eb); display: flex; align-items: center; justify-content: center; font-size: 13px; }
.adm-brand-label { font-size: 14px; font-weight: 600; color: var(--text); }
.adm-avatar { margin-right: 16px; width: 30px; height: 30px; border-radius: 50%; background: linear-gradient(135deg, #2563eb, #7c3aed); display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 700; color: #fff; flex-shrink: 0; }
.adm-tabs { display: flex; align-items: center; gap: 2px; padding: 0 20px; border-bottom: 1px solid var(--border2); background: var(--surface); overflow-x: auto; }
.adm-tab { padding: 14px 16px; font-size: 13px; font-weight: 500; color: var(--muted); background: none; border: none; cursor: pointer; border-bottom: 2px solid transparent; margin-bottom: -1px; transition: color .15s; font-family: inherit; white-space: nowrap; }
.adm-tab:hover { color: var(--text2); }
.adm-tab.active { color: var(--accent); font-weight: 700; border-bottom-color: var(--accent); }
.adm-main { max-width: 1100px; margin: 0 auto; padding: 28px 24px 80px; }
.adm-error { padding: 10px 14px; border-radius: 7px; background: rgba(248,113,113,.08); border: 1px solid rgba(248,113,113,.2); color: #dc2626; font-size: 13px; margin-bottom: 14px; }
.adm-spinner-wrap { display: flex; justify-content: center; padding: 40px 0; }
.adm-spinner { width: 26px; height: 26px; border-radius: 50%; border: 3px solid var(--border2); border-top-color: var(--accent); animation: spin .7s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
.adm-action-btn { background: none; border: 1px solid var(--border); border-radius: 6px; padding: 5px 11px; font-size: 12px; color: var(--text2); cursor: pointer; font-family: inherit; transition: border-color .15s, color .15s; white-space: nowrap; }
.adm-action-btn:hover { border-color: var(--accent); color: var(--accent); }

.dash-head { display: flex; justify-content: space-between; align-items: flex-end; flex-wrap: wrap; gap: 10px; margin-bottom: 22px; }
.dash-head h1 { font-family: inherit; font-size: 20px; font-weight: 700; color: var(--text); }
.dash-head p { font-size: 12.5px; color: var(--muted); margin-top: 3px; }
.dash-asof { font-size: 11px; color: var(--muted); font-variant-numeric: tabular-nums; }

.dash-section { margin-bottom: 26px; }
.dash-section-title { font-size: 12px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; color: var(--text2); margin-bottom: 12px; padding-left: 2px; }
.dash-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; }
@media(max-width: 900px) { .dash-grid { grid-template-columns: repeat(2, 1fr); } }
@media(max-width: 520px) { .dash-grid { grid-template-columns: 1fr; } }

.stat { background: var(--surface); border: 1px solid var(--border); border-left: 4px solid var(--c, var(--accent)); border-radius: 9px; padding: 15px 16px 13px; }
.stat .v { font-size: 22px; font-weight: 800; color: var(--c, var(--text)); line-height: 1.15; font-variant-numeric: tabular-nums; word-break: break-word; }
.stat .k { font-size: 11px; font-weight: 600; color: var(--text2); margin-top: 7px; text-transform: uppercase; letter-spacing: .03em; }
.stat .sub { font-size: 10.5px; color: var(--muted); margin-top: 3px; }
.stat.clickable { cursor: pointer; transition: transform .12s, box-shadow .12s; }
.stat.clickable:hover { transform: translateY(-1px); box-shadow: 0 6px 16px -8px rgba(13,26,54,.18); }

.dash-note { font-size: 11.5px; color: var(--muted); background: var(--surface2); border: 1px solid var(--border2); border-radius: 8px; padding: 10px 13px; margin-top: 10px; line-height: 1.5; }
`;

const MONTHS = ["", "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];

function omr(n) {
  if (n == null) return "—";
  return "OMR " + Number(n).toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 });
}
function signedOmr(n) {
  if (n == null) return "—";
  const abs = Math.abs(Number(n)).toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 });
  return (n > 0 ? "+OMR " : n < 0 ? "−OMR " : "OMR ") + abs;
}

function Stat({ v, k, sub, color, onClick }) {
  return (
    <div className={`stat${onClick ? " clickable" : ""}`} style={color ? { "--c": color } : undefined} onClick={onClick}>
      <div className="v">{v}</div>
      <div className="k">{k}</div>
      {sub && <div className="sub">{sub}</div>}
    </div>
  );
}

export default function HrmsDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const initials = (user?.name || "H").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/reports/dashboard");
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load the dashboard.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const wf = data?.workforce;
  const pay = data?.payroll;
  const comp = data?.compliance;
  const cur = pay?.current_period;
  const prev = pay?.previous_period;

  return (
    <>
      <style>{CSS}</style>
      <header className="adm-topbar">
        <button className="adm-back" onClick={() => navigate("/")}>&#8592;</button>
        <div className="adm-brand">
          <div className="adm-brand-mark">📊</div>
          <span className="adm-brand-label">HRMS & Payroll — Dashboard</span>
        </div>
        <div style={{ marginRight: 8, fontSize: 12, color: "#6080aa" }}>{user?.org_name}</div>
        <div className="adm-avatar" title={user?.name}>{initials}</div>
      </header>

      <div className="adm-tabs">
        <button className="adm-tab active">Dashboard</button>
        <button className="adm-tab" onClick={() => navigate("/hrms/employees")}>Employees</button>
        <button className="adm-tab" onClick={() => navigate("/hrms/payroll")}>Payroll</button>
        <button className="adm-tab" onClick={() => navigate("/hrms/expiry")}>Document Expiry</button>
        <button className="adm-tab" onClick={() => navigate("/hrms/data-quality")}>Data Quality</button>
        <button className="adm-tab" onClick={() => navigate("/hrms/company-documents")}>Company Documents</button>
        <button className="adm-tab" onClick={() => navigate("/hrms/assistant")}>AI Assistant</button>
      </div>

      <div className="adm-main">
        {error && <div className="adm-error">{error}</div>}

        <div className="dash-head">
          <div>
            <h1>Management Dashboard</h1>
            <p>Workforce, payroll and compliance at a glance.</p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {data && <div className="dash-asof">As of {new Date(data.generated_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}</div>}
            <button className="adm-action-btn" onClick={load} disabled={loading}>{loading ? "Refreshing…" : "Refresh"}</button>
          </div>
        </div>

        {loading ? (
          <div className="adm-spinner-wrap"><div className="adm-spinner" /></div>
        ) : data && (
          <>
            <div className="dash-section">
              <div className="dash-section-title">Workforce</div>
              <div className="dash-grid">
                <Stat v={wf.total_employees} k="Total Employees" color="var(--accent)"
                      onClick={() => navigate("/hrms/employees")} />
                <Stat v={wf.active_employees} k="Active Employees" color="var(--ok)" />
                <Stat v={wf.payroll_employees} k="Payroll Employees" />
                <Stat v={wf.non_payroll_employees} k="Non-Payroll Employees" />
                <Stat v={wf.offshore_employees} k="Offshore Employees" />
                <Stat v={wf.new_joiners_this_month} k="New Joiners" sub="this month" color="var(--ok)" />
                <Stat v={wf.departures_this_month} k="Resignations / Terminations" sub="this month"
                      color={wf.departures_this_month > 0 ? "var(--warn)" : undefined} />
              </div>
            </div>

            <div className="dash-section">
              <div className="dash-section-title">
                Payroll{cur && ` — ${MONTHS[cur.period_month]} ${cur.period_year}`}
              </div>
              {!cur ? (
                <div className="dash-note">No payroll period has been created yet.</div>
              ) : (
                <>
                  <div className="dash-grid">
                    <Stat v={omr(cur.net_payroll)} k="Current Month Net Payroll" color="var(--accent)"
                          onClick={() => navigate("/hrms/payroll")} />
                    <Stat v={prev ? omr(prev.net_payroll) : "—"} k="Previous Month Payroll"
                          sub={prev ? `${MONTHS[prev.period_month]} ${prev.period_year}` : "no earlier period"} />
                    <Stat v={signedOmr(pay.difference)} k="Payroll Difference"
                          color={pay.difference == null ? undefined : pay.difference > 0 ? "var(--warn)" : "var(--ok)"} />
                    <Stat v={omr(cur.gross_payroll)} k="Gross Payroll" />
                    <Stat v={omr(cur.total_deductions)} k="Deductions" />
                    <Stat v={omr(pay.average_net_salary)} k="Average Salary"
                          sub={`across ${cur.employee_count} employee${cur.employee_count === 1 ? "" : "s"}`} />
                  </div>
                  {["draft", "calculated", "reviewed"].includes(cur.status) && (
                    <div className="dash-note">
                      This period is still <strong>{cur.status}</strong> — figures may change until payroll is approved.
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="dash-section">
              <div className="dash-section-title">Compliance</div>
              <div className="dash-grid">
                <Stat v={comp.civil_ids_expiring} k="Civil IDs Expiring"
                      sub={`expired or within ${comp.expiry_window_days} days`}
                      color={comp.civil_ids_expiring > 0 ? "var(--danger)" : "var(--ok)"}
                      onClick={() => navigate("/hrms/expiry")} />
                <Stat v={comp.passports_expiring} k="Passports Expiring"
                      sub={`expired or within ${comp.expiry_window_days} days`}
                      color={comp.passports_expiring > 0 ? "var(--danger)" : "var(--ok)"}
                      onClick={() => navigate("/hrms/expiry")} />
                <Stat v={comp.contracts_expiring} k="Contracts Expiring"
                      sub={`expired or within ${comp.expiry_window_days} days`}
                      color={comp.contracts_expiring > 0 ? "var(--danger)" : "var(--ok)"}
                      onClick={() => navigate("/hrms/expiry")} />
                <Stat v={comp.missing_information} k="Missing Employee Information"
                      sub="employees with data gaps"
                      color={comp.missing_information > 0 ? "var(--warn)" : "var(--ok)"}
                      onClick={() => navigate("/hrms/data-quality")} />
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
